import {Flex, Text} from '@priority-software/priority-style-react';
import React, {useState, useRef, useEffect, useLayoutEffect} from 'react';
import cx from 'classnames';
import styles from '../styles.module.scss';
import {ChartConfig} from '../../types';
import {genKPIChartData} from '../../serialize';
import {reportWebSdkService} from '../../services/reportWebSdkService';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import {CircularLoader} from '../../../../components/CircularLoader/CircularLoader';
import {prepareInputsData} from './utils';
import {useChartContext} from '../../contexts/useChartContext';

export const KPI = ({config}: {config: ChartConfig}) => {
    const [isLoadingExplanatoryReport, setIsLoading] = useState<boolean>(false);
    const { showAnalysisPanel } = useChartContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    
    // Create a unique key that changes when panel visibility or container dimensions change
    const kpiKey = `kpi-${showAnalysisPanel ? 'panel-visible' : 'panel-hidden'}-${containerDimensions.width}-${containerDimensions.height}`;
    
    const value = genKPIChartData(config);
    
    // Set up resize observer to detect container size changes
    useLayoutEffect(() => {
        if (!containerRef.current) return undefined;
        
        // Create a ResizeObserver to monitor container size changes
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                
                // Only update if dimensions actually changed
                if (width !== containerDimensions.width || height !== containerDimensions.height) {
                    console.log('KPI container dimensions changed:', width, height);
                    setContainerDimensions({ width, height });
                }
            }
        });
        
        // Start observing the container
        resizeObserver.observe(containerRef.current);
        
        // Clean up observer on unmount
        return () => resizeObserver.disconnect();
    }, []);
    
    // Handle panel visibility changes
    useEffect(() => {
        console.log('Analysis panel visibility changed for KPI:', showAnalysisPanel);
    }, [showAnalysisPanel]);

    const onDataPlotClick = async () => {
        const {currentScreen} = reportGeneratorsStore;
        if (config.explanatoryReport && currentScreen) {
            setIsLoading(true);
            const reportInputsMetaData = prepareInputsData(config);
            currentScreen.setCurrExpRepTitle(config.explanatoryReport.Title);
            currentScreen.setExpReportInputsMetaData(reportInputsMetaData);
            await reportWebSdkService.startProc();
            setIsLoading(false);
        }
    };

    return (
        <>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} key={kpiKey}>
                <Flex
                    direction="column"
                    justifyContent="space-between"
                    alignItems="center"
                    componentClasses={styles.kpiContainer}
                    style={{ transition: 'width 0.3s ease-in-out' }}
                >
                <Text skin="caption-1" style={{fontSize: '32px'}}>
                    {config?.chartName || ''}
                </Text>
                <Text
                    skin="caption-2"
                    componentClasses={cx(styles.kpiValue, {
                        [styles.clickableValue]: config?.explanatoryReport
                    })}
                    style={{fontSize: '120px'}}
                    onClick={onDataPlotClick}
                >
                    {value}
                </Text>
                </Flex>
            </div>
            {isLoadingExplanatoryReport && (
                <div style={{position: 'absolute'}}>
                    <CircularLoader />
                </div>
            )}
        </>
    );
};
